import React from 'react';
import { Link } from 'react-router-dom';
import { LuZap, LuChevronRight } from 'react-icons/lu';
import Card from '../../../components/ui/Card/Card';
import Badge from '../../../components/ui/Badge/Badge';
import CurrencyAmount from '../../../components/ui/CurrencyAmount/CurrencyAmount';
import { sessionStatusCopy } from '../../../utils/statusCopy';
import { resolveBillingDisplay } from '../../../utils/billingDisplay';
import { formatSessionDate, formatSessionTime } from '../../../utils/formatDate';
import { presentBilling, stateCopy } from '../../../chargerUx/copy';
import './HistorySessionCard.less';

/**
 * Compact single-row card for the Charging History list.
 *
 * Three-zone layout, vertically centred:
 *
 *   [ ⚡ ]  Station name        [ Completed ]                 [ › ]
 *          Jul 13, 2026 · 10:45 AM
 *          0.04 kWh · Session amount $5.00 BBD
 *          Fleet session · Pending invoice
 *
 * The amount, its label, the session type and the billing status are
 * the backend's `billing.display`; the card decides none of them.
 *
 * Distinct from the dashboard's `ChargingSessionCard`, which stays
 * on its current (wider) layout for the "Recent activity" section
 * so this task doesn't touch the dashboard.
 *
 * Interaction: the whole card is a link when `to` is provided. Native
 * `<a>` semantics give us keyboard focus, focus-visible ring, and
 * accessible name for free. Falls back to a non-interactive div when
 * no target is given (defensive — production always passes `to`).
 */
/** "42 min" / "1 h 05 min", or null when either end is unknown. */
function durationLabel(startedAt, endedAt) {
  const start = Date.parse(startedAt);
  const end = Date.parse(endedAt);
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return null;
  const minutes = Math.round((end - start) / 60000);
  if (minutes < 1) return 'Under 1 min';
  if (minutes < 60) return `${minutes} min`;
  return `${Math.floor(minutes / 60)} h ${String(minutes % 60).padStart(2, '0')} min`;
}

function HistorySessionCard({ session, to }) {
  const status = sessionStatusCopy(
    session.paymentStatus === 'paid'
      ? 'completed'
      : session.endedAt
      ? 'completed'
      : 'pending',
  );
  // Charger UX: when the API provides the session's canonical outcome, the
  // row shows that outcome, a short reason, and whether energy and cost are
  // final. Without it the row renders exactly as before.
  const canonical = session.canonical || null;
  const outcome = canonical ? stateCopy(canonical.sessionState) : null;
  const shortReason = canonical && typeof canonical.message?.cause === 'string' ? canonical.message.cause : null;
  // Whether the figures are final yet (charger UX). The amount, its label,
  // the session type and the billing status stay the backend's billing display.
  const canonicalBilling = canonical ? presentBilling(canonical) : null;
  const badge = outcome || status;
  const durationStr = durationLabel(session.startedAt, session.endedAt);
  const cost = session.capturedCents ?? null;
  const currency = session.currency || 'BBD';
  const startTs = session.startedAt || session.endedAt;
  const stationName = session.stationName || 'Charging session';
  const dateStr = formatSessionDate(startTs);
  const timeStr = formatSessionTime(startTs);
  const energyStr =
    typeof session.energyKwh === 'number'
      ? `${session.energyKwh.toFixed(2)} kWh`
      : null;

  const billing = resolveBillingDisplay(session, {
    amount: <CurrencyAmount cents={cost} currency={currency} />,
    paymentStatus: session.paymentStatus,
  });

  return (
    <Card
      as={to ? Link : 'div'}
      to={to}
      // Deliberately NOT passing `interactive` — that adds
      // `.is-interactive` to the Card, whose `display: block` in
      // `Card.less` would clobber our grid layout and slot the
      // chevron under the body instead of to its right. We render
      // the same hover / focus / cursor affordances locally in
      // `.gc-history-row` LESS.
      className="gc-history-row"
      aria-label={`${stationName} — ${badge.label}${shortReason ? `. ${shortReason}` : ''}`}
    >
      <span className="gc-history-row-icon" aria-hidden="true">
        <LuZap />
      </span>

      <div className="gc-history-row-body">
        <div className="gc-history-row-heading">
          <h3 className="gc-history-row-title">{stationName}</h3>
          <Badge tone={badge.tone} className="gc-history-row-badge">
            {badge.label}
          </Badge>
        </div>

        {shortReason && (
          <p className="gc-history-row-reason" data-testid="history-row-reason">{shortReason}</p>
        )}

        {(dateStr || timeStr) && (
          <p className="gc-history-row-when">
            {dateStr}
            {dateStr && timeStr ? ' · ' : ''}
            {timeStr}
            {durationStr && <span data-testid="history-row-duration"> · {durationStr}</span>}
          </p>
        )}

        {canonicalBilling && canonicalBilling.status === 'PENDING' ? (
          // Not final yet: say so instead of showing a figure as if it were.
          <p className="gc-history-row-meta" data-testid="history-row-pending">
            <span className="gc-history-row-cost">{canonicalBilling.label}</span>
          </p>
        ) : canonicalBilling && canonicalBilling.status === 'NOT_APPLICABLE' ? (
          <p className="gc-history-row-meta" data-testid="history-row-not-charged">
            <span className="gc-history-row-cost">{canonicalBilling.label}</span>
          </p>
        ) : (
          <p className="gc-history-row-meta">
            {energyStr && <span>{energyStr}</span>}
            {energyStr && <span aria-hidden="true"> · </span>}
            <span className="gc-history-row-amount">
              {billing.amount.label}{' '}
              <span className="gc-history-row-cost">{billing.amount.value}</span>
            </span>
          </p>
        )}

        {billing.fromServer && (
          <p className="gc-history-row-billing">
            {billing.sessionTypeLabel && <span>{billing.sessionTypeLabel}</span>}
            {billing.sessionTypeLabel && <span aria-hidden="true"> · </span>}
            <span className="gc-history-row-status">
              {billing.status.label}
            </span>
          </p>
        )}
      </div>

      {to && (
        <LuChevronRight
          aria-hidden="true"
          className="gc-history-row-chevron"
        />
      )}
    </Card>
  );
}

export default HistorySessionCard;
